import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { DEFAULT_SELECTED_METRICS, STATISTICS_METRICS } from './statisticsMetrics';

export const STATISTICS_DEFAULT_CONTEXT = {
  enabledStatistics: DEFAULT_SELECTED_METRICS,
};

const StatisticsWidgetEdit = ({ context, onChange }) => {
  // eslint-disable-next-line react/destructuring-assignment
  const selected = context.enabledStatistics || [];

  const commit = (next) => {
    onChange({ ...context, enabledStatistics: next });
  };

  // Change the stat at a given row. Ignores no-ops and prevents duplicates
  // (the options are already filtered, but guard defensively).
  const changeStatAt = (index, key) => {
    if (selected[index] === key) return;
    if (selected.includes(key)) return;
    const next = [...selected];
    next[index] = key;
    commit(next);
  };

  const removeStatAt = (index) => {
    // Backend requires @NotEmpty; block removing the final stat.
    if (selected.length <= 1) return;
    const next = selected.filter((_, i) => i !== index);
    commit(next);
  };

  // First metric not already chosen, used when adding a new row.
  const firstUnusedKey = STATISTICS_METRICS
    .map((m) => m.key)
    .find((key) => !selected.includes(key));

  const addStat = () => {
    if (!firstUnusedKey) return;
    commit([...selected, firstUnusedKey]);
  };

  const allStatsUsed = !firstUnusedKey;

  return (
    <div className="statistics-widget-edit">
      <div className="form-group row align-items-start mb-2">
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className="col-3 col-form-label font-weight-bold pt-0">Stats</label>
        <div className="col-9">
          {selected.map((selectedKey, index) => {
            // Options for this row: its own value plus any stat not used elsewhere.
            const options = STATISTICS_METRICS.filter(
              (m) => m.key === selectedKey || !selected.includes(m.key),
            );
            const selectId = `statistics-stat-${index}`;
            const canRemove = selected.length > 1;

            return (
            // eslint-disable-next-line react/no-array-index-key
              <div className="d-flex align-items-center mb-2" key={`${selectedKey}-${index}`}>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className="sr-only" htmlFor={selectId}>
                  {`Statistic ${index + 1}`}
                </label>
                <select
                  id={selectId}
                  className="form-control form-control-sm"
                  value={selectedKey}
                  onChange={(e) => changeStatAt(index, e.target.value)}
                >
                  {options.map((metric) => (
                    <option key={metric.key} value={metric.key}>
                      {metric.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-link btn-sm text-secondary statistics-widget-edit__remove ml-1"
                  onClick={() => removeStatAt(index)}
                  disabled={!canRemove}
                  aria-label={`Remove statistic ${index + 1}`}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            );
          })}

          <button
            type="button"
            className="btn btn-link btn-sm p-0 statistics-widget-edit__add"
            onClick={addStat}
            disabled={allStatsUsed}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            Add Stat
          </button>
        </div>
      </div>
    </div>
  );
};

StatisticsWidgetEdit.propTypes = {
  context: PropTypes.shape({
    enabledStatistics: PropTypes.arrayOf(PropTypes.string),
  }),
  onChange: PropTypes.func,
};

StatisticsWidgetEdit.defaultProps = {
  context: STATISTICS_DEFAULT_CONTEXT,
  onChange: () => {},
};

export default StatisticsWidgetEdit;
